import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { rpc } from "../../../lib/rpc";
import { useFlowOperationId, uuid4 } from "../../../hooks/useFlowOperationId";
import { useScanPrefill } from "../../../hooks/useScanPrefill";
import { FlowShell } from "../../../components/mobile/FlowShell";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const ANTI_BIAS_KEY = "platzwart.antiBias";

export default function InventoryCountFlow() {
    const prefill = useScanPrefill();
    const [inventories, setInventories] = useState([]);
    const [invId, setInvId] = useState("");
    const [items, setItems] = useState([]);
    const [positions, setPositions] = useState({});
    const [articles, setArticles] = useState({});
    const [compartments, setCompartments] = useState({});
    const antiBias = useMemo(() => localStorage.getItem(ANTI_BIAS_KEY) === "true", []);
    const [counts, setCounts] = useState({}); // item_id -> counted
    const [busy, setBusy] = useState(false);
    const [activeItemId, setActiveItemId] = useState("");
    const [prefillApplied, setPrefillApplied] = useState(false);

    useEffect(() => {
        Promise.all([
            rpc.get("/inventories"),
            rpc.get("/master/stock-positions"),
            rpc.get("/master/articles"),
            rpc.get("/master/compartments"),
        ]).then(([inv, p, a, c]) => {
            if (inv.ok) setInventories(inv.data.filter((i) => i.status === "open"));
            if (p.ok) setPositions(Object.fromEntries(p.data.map((x) => [x.id, x])));
            if (a.ok) setArticles(Object.fromEntries(a.data.map((x) => [x.id, x])));
            if (c.ok) setCompartments(Object.fromEntries(c.data.map((x) => [x.id, x])));
        });
    }, []);

    useEffect(() => {
        if (!invId) return;
        rpc.get(`/inventories/${invId}/items`).then((r) => {
            if (r.ok) {
                setItems(r.data);
                setActiveItemId((current) => current || r.data[0]?.id || "");
            }
        });
    }, [invId]);

    useEffect(() => {
        if (prefillApplied || !prefill?.compartmentId || items.length === 0) return;
        const match = items.find((item) =>
            positions[item.stock_position_id]?.compartment_id === prefill.compartmentId
        );
        if (match) {
            setActiveItemId(match.id);
            document.querySelector(`[data-testid="inv-item-${match.id}"]`)?.scrollIntoView({ block: "center" });
            toast.success("Inventurposition aus Fach-Scan fokussiert.");
        } else {
            toast.warning("Für das gescannte Fach gibt es kein offenes Inventur-Item.");
        }
        setPrefillApplied(true);
    }, [prefill, prefillApplied, items, positions]);

    const sendCount = async (item) => {
        const q = counts[item.id];
        if (q === undefined || q === "") return toast.error("Menge eingeben.");
        setBusy(true);
        const res = await rpc.post(`/inventories/items/${item.id}/count`, {
            client_operation_id: uuid4(), // fresh per item
            counted_quantity: Number(q),
        });
        setBusy(false);
        if (!res.ok) return toast.error(res.error);
        toast.success("Zählung übernommen.");
        rpc.get(`/inventories/${invId}/items`).then((r) => r.ok && setItems(r.data));
    };

    return (
        <FlowShell
            testId="flow-inventory-count"
            title="Inventur-Zählung"
            kind="Step-Wizard"
            subtitle={antiBias ? "Anti-Bias: Soll-Menge verborgen" : "Soll-Menge sichtbar"}
            onSubmit={activeItemId ? () => sendCount(items.find((item) => item.id === activeItemId)) : undefined}
            submitLabel="Zählung übernehmen"
            submittingLabel="Zählung läuft..."
            submitDisabled={!activeItemId || counts[activeItemId] === undefined || counts[activeItemId] === ""}
            submitting={busy}
            submitTestId="inv-count-active-submit"
        >
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Offene Inventur
                </Label>
                <Select value={invId} onValueChange={setInvId}>
                    <SelectTrigger className="h-12" data-testid="inv-select">
                        <SelectValue placeholder="Inventur wählen" />
                    </SelectTrigger>
                    <SelectContent>
                        {inventories.map((i) => (
                            <SelectItem key={i.id} value={i.id}>
                                {i.name || i.id.slice(0, 8)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="flex flex-col gap-2" data-testid="inv-items">
                {items.length === 0 ? (
                    <div className="rounded-lg border border-border/60 bg-card/40 px-3 py-4 text-center text-xs text-muted-foreground">
                        Wähle eine Inventur — Items werden geladen.
                    </div>
                ) : (
                    items.map((it) => {
                        const pos = positions[it.stock_position_id];
                        const art = pos && articles[pos.article_id];
                        const compartment = pos && compartments[pos.compartment_id];
                        return (
                            <div
                                key={it.id}
                                className={`rounded-lg border bg-card/40 p-3 ${activeItemId === it.id ? "border-primary" : "border-border"}`}
                                data-testid={`inv-item-${it.id}`}
                                onClick={() => setActiveItemId(it.id)}
                            >
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-medium text-foreground">
                                        {art?.name || "Artikel"}
                                    </span>
                                    {!antiBias ? (
                                        <span className="text-muted-foreground">
                                            Soll: {it.expected_quantity}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="mt-1 text-xs font-medium text-primary" data-testid={`inv-location-${it.id}`}>
                                    Lagerort: {compartment?.code || compartment?.name || "nicht zugeordnet"}
                                </div>
                                <div className="mt-2 flex gap-2">
                                    <Input
                                        type="number"
                                        inputMode="numeric"
                                        aria-label="Gezählte Menge"
                                        value={counts[it.id] ?? ""}
                                        onChange={(e) =>
                                            setCounts((c) => ({ ...c, [it.id]: e.target.value }))
                                        }
                                        placeholder="gezählt"
                                        className="h-11"
                                        data-testid={`inv-count-${it.id}`}
                                    />
                                </div>
                                {it.counted_quantity !== null &&
                                it.counted_quantity !== undefined ? (
                                    <div className="mt-2 text-xs text-muted-foreground">
                                        Bereits gezählt: {it.counted_quantity}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })
                )}
            </div>
        </FlowShell>
    );
}
